import { API_BASE, getApiHeaders } from './apiClient';

export interface IAPAction {
  action: string;
  /** Free-text description — extra context on what "Action" means in this
   *  incident (e.g. acceptance criteria, dependencies, stakeholders).
   *  Legacy rows may not have this field; UI falls back to empty string. */
  description?: string;
  assignee: string;
  deadline: string;
  status: 'Pending' | 'Complete' | 'In Progress' | 'On-going' | 'Issue' | 'Not Started';
  priority: 'High' | 'Medium' | 'Low';
}

export interface IAPStrategy {
  strategy: string;
  /** Free-text description — the why / scope behind the strategy.
   *  Optional so legacy rows don't break. */
  description?: string;
  owner: string;
  timeline: string;
}

export interface IAPResource {
  resource: string;
  quantity: string;
  status: 'Available' | 'Requested' | 'Deployed';
}

export interface IAPCommunication {
  audience: string;
  message: string;
  channel: string;
  frequency: string;
}

export interface IAPSignature {
  /** Display name from cima_staff (first + last). Falls back to email handle. */
  name: string | null;
  email: string | null;
  jobTitle: string | null;
  /** System role at sign-off time (ADMIN/MANAGER/USER). */
  role: string | null;
}

export interface IAP {
  incidentId: string;
  objectives: string[];
  strategies: IAPStrategy[];
  actions: IAPAction[];
  resources: IAPResource[];
  communications: IAPCommunication[];
  safetyConsiderations: string;
  reviewSchedule: string;
  status: 'DRAFT' | 'IN_REVIEW' | 'SIGNED_OFF';
  signedOffBy?: number;
  signedOffAt?: string;
  /** Resolved details about the signer for display in the UI + XLSX. */
  signature?: IAPSignature | null;
  updatedAt: string;
}

async function apiFetch(url: string, opts?: RequestInit) {
  const res = await fetch(url, { ...opts, headers: { ...getApiHeaders(), ...(opts?.headers ?? {}) } });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

export async function getIAP(incidentId: string): Promise<IAP | null> {
  const data = await apiFetch(`${API_BASE}/cima/incidents/${incidentId}/iap`);
  return data.iap;
}

export async function updateIAPField(incidentId: string, field: string, value: any): Promise<IAP> {
  const data = await apiFetch(`${API_BASE}/cima/incidents/${incidentId}/iap`, {
    method: 'PATCH', body: JSON.stringify({ field, value }),
  });
  return data.iap;
}

export async function signOffIAP(incidentId: string, signedByName: string): Promise<void> {
  await apiFetch(`${API_BASE}/cima/incidents/${incidentId}/iap/sign-off`, {
    method: 'POST',
    body: JSON.stringify({ signedByName }),
  });
}
